import React, {useEffect, useState} from "react";
import Card from "components/Cards/Card";
import ToggleButton from "components/ui/ToggleButton";
import keyQuestionsIcon from "assets/Icon_Tab_10.png";
import {setChartSelection, getChartSelection} from "helpers/chartSelections";
import {v4 as uuidv4} from "uuid";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
  Legend
} from "recharts";
import {useCurrentPng} from "recharts-to-png";
import {ImageMap} from "../../Dashboard/Dashboard";
import reviewSummaryIcon from "../../../assets/Icon_Tab_08.png";
import LoadingIndicator from "../../ui/LoadingIndicator";
import { YOY_BAR_CHART_COLORS, YOY_LINE_CHART_COLORS } from "../../../constants/chartColors";
import PortalTooltip from "../PortalTooltip";

type KeyQsYoYProps = {
  href: string | null;
  data: ChartDataYoY;
  minWidth: string;
  defaultChartType: string;
  customName?: string;
  setImages?: React.Dispatch<React.SetStateAction<ImageMap>>;
  idx?: number;
  isLoading?: boolean;
};

type ChartDataYoY = {
  pieChartData: any;
  otherChartData: any[];
  options: {
    yAxisType: "Count" | "Percentage" | "Score";
  };
  questions?: string[];
  periods?: string[];
};

// Use different colors based on chart type
const getChartColors = (chartType: string) => {
  return chartType === "Bar" ? YOY_BAR_CHART_COLORS : YOY_LINE_CHART_COLORS;
};

const checkNullChartData = (c: any[]) => {
  const data = c.find((v) => v);
  return !!data;
};

function percentageFormatter(item: any) {
  return parseFloat(item).toFixed(1) + "%";
}

const Tick: React.FC<any> = ({
  payload: { value },
  verticalAnchor,
  visibleTicksCount,
  ...rest
}) => (
  <Text
    {...rest}
    verticalAnchor={verticalAnchor}
    visibleTicksCount={visibleTicksCount}
    width={80}
    style={{ fontSize: '11px' }}
  >
    {value}
  </Text>
);

const ticks20 = [0, 20, 40, 60, 80, 100];

function getMinValue(data: any[], keys?: string[]): number {
  let min = 0;
  if (!data || !data.length) return 0;
  data.forEach((item: any) => {
    if (keys && keys.length) {
      keys.forEach((k) => {
        const v = item[k];
        if (typeof v === "number" && v < min) min = v;
      });
    } else {
      Object.entries(item).forEach(([k, v]) => {
        if (k !== "name" && typeof v === "number" && (v as number) < min) min = v as number;
      });
    }
  });
  return min;
}

function getPercentageDomain(data: any[], keys?: string[]): [number, number] {
  const min = getMinValue(data, keys);
  if (min >= 0) return [0, 100];
  const roundedMin = Math.floor(min / 20) * 20;
  return [Math.max(roundedMin, -100), 100];
}

function getPercentageTicks(data: any[], keys?: string[]): number[] {
  const min = getMinValue(data, keys);
  if (min >= 0) return ticks20;
  const roundedMin = Math.floor(min / 20) * 20;
  const start = Math.max(roundedMin, -100);
  const ticks: number[] = [];
  for (let i = start; i <= 100; i += 20) {
    ticks.push(i);
  }
  return ticks;
}

function scoreFormatter(item: any) {
  return parseFloat(item).toFixed(2);
}

const scoreTicks = [0, 1, 2, 3, 4, 5];
const scoreDomain: [number, number] = [0, 5];

function getYAxisFormatter(yAxisType: string | undefined) {
  if (yAxisType === "Score") return scoreFormatter;
  return percentageFormatter;
}

function getYAxisDomain(yAxisType: string | undefined, data: any[], keys?: string[]) {
  if (yAxisType === "Score") return scoreDomain;
  return getPercentageDomain(data, keys);
}

function getYAxisTicks(yAxisType: string | undefined, data: any[], keys?: string[]) {
  if (yAxisType === "Score") return scoreTicks;
  return getPercentageTicks(data, keys);
}

function getValueFormatter(yAxisType: string | undefined) {
  if (yAxisType === "Score") return scoreFormatter;
  return percentageFormatter;
}

const KeyQuestionsYoY: React.FC<KeyQsYoYProps> = ({
  href,
  data,
  minWidth,
  defaultChartType,
  customName,
  setImages,
  idx,
  isLoading = true
}) => {
  const chartTypeId = `keyQuestionsYoY_chartType_${customName || "default"}`;
  const togglesId = `keyQuestionsYoY_toggles_${customName || "default"}`;

  const [keyQuestionsChartType, setKeyQuestionsChartType] = useState(
    () => getChartSelection(chartTypeId) || defaultChartType || "Bar"
  );

  const handleSetKeyQuestionsChartType = (value: string) => {
    setKeyQuestionsChartType(value);
    setChartSelection(chartTypeId, value);
  };

  const [keyQuestionsPeriodToggles, setKeyQuestionsPeriodToggles] = useState<
    Record<string, boolean>
  >({});

  // Track internal loading state
  const [internalLoading, setInternalLoading] = useState(true);

  useEffect(() => {
    const periods: Record<string, boolean> = {};
    if (data && data.periods) {
      for (const period of data.periods) {
        // For YoY, we want both periods visible by default
        periods[period] = true;
      }
    }
    const storedToggles = getChartSelection(togglesId);
    if (storedToggles && typeof storedToggles === "object") {
      const merged: Record<string, boolean> = {};
      for (const key of Object.keys(periods)) {
        merged[key] = storedToggles.hasOwnProperty(key) ? storedToggles[key] : periods[key];
      }
      setKeyQuestionsPeriodToggles(merged);
      setChartSelection(togglesId, merged);
    } else {
      setKeyQuestionsPeriodToggles(periods);
      setChartSelection(togglesId, periods);
    }
  }, [data]);

  // Effect to check if data is ready
  useEffect(() => {
    if (data && data.otherChartData) {
      // Set a small delay to prevent flashing
      const timer = setTimeout(() => {
        setInternalLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [data]);

  const handleKeyQuestionsPeriodToggle = (t: string) => {
    setKeyQuestionsPeriodToggles((keyQuestionsPeriodToggles) => {
      const toggles = { ...keyQuestionsPeriodToggles };
      toggles[t] = !toggles[t];
      setChartSelection(togglesId, toggles);
      return toggles;
    });
  };

  // Listen for sync event from PDF generator
  useEffect(() => {
    const handleSync = () => {
      const storedChartType = getChartSelection(chartTypeId);
      if (storedChartType) setKeyQuestionsChartType(storedChartType);
      const storedToggles = getChartSelection(togglesId);
      if (storedToggles) setKeyQuestionsPeriodToggles(storedToggles);
    };
    window.addEventListener("pdf-sync-selections", handleSync);
    return () => window.removeEventListener("pdf-sync-selections", handleSync);
  }, [chartTypeId, togglesId]);

  // Determine if we should show loading state
  const showLoading = isLoading || internalLoading;

  const uuid = uuidv4();

  const [getPng, { ref }] = useCurrentPng();
  const [savedPng, setSavedPng] = useState<boolean>(false);

  async function saveChart() {
    let runningInterval = setInterval(async () => {
      if (savedPng) {
        clearInterval(runningInterval);
      }
      try {
        const png = await getPng();

        if (png && setImages && idx) {
          setImages((oldState: ImageMap) => {
            return {...oldState, [idx]: png}
          });
          setSavedPng(true);
          clearInterval(runningInterval);
        }
      }
      catch(error) {
        // Handle error silently
      }
    }, 1000)
  }

  // Transform data for grouped bar chart
  const transformDataForGroupedBars = () => {
    if (!data || !data.otherChartData || !data.periods) return [];
    
    // For grouped bars, we need to restructure the data
    // Each question should have values for both periods as separate properties
    return data.otherChartData;
  };

  if (showLoading) {
    return (
        <Card
            href={href}
            icon={keyQuestionsIcon}
            title={customName ? customName : "KEY QUESTIONS YEAR ON YEAR"}
            chartOptions={
              <React.Fragment>
                <button
                    className="Option"
                    onClick={() => handleSetKeyQuestionsChartType("Bar")}
                >
                  Bar
                </button>
                <button
                    className="Option"
                    onClick={() => handleSetKeyQuestionsChartType("Line")}
                >
                  Line
                </button>
              </React.Fragment>
            }
        >
          <div className="CardBody">
            <div className="ToggleFlex">
              <div className="BarToggle">
                {keyQuestionsPeriodToggles &&
                    Object.entries(keyQuestionsPeriodToggles).map(
                        ([key, value], index: number) => {
                          return (
                              <ToggleButton
                                  key={index}
                                  active={value}
                                  toggle={() => handleKeyQuestionsPeriodToggle(key)}
                              >
                                <div
                                    className="ToggleDot"
                                    style={{
                                      backgroundColor: getChartColors(keyQuestionsChartType)[index],
                                    }}
                                ></div>
                                <div className="ToggleLabel">{key}</div>
                              </ToggleButton>
                          );
                        }
                    )}
              </div>
            </div>
            <div className="Chart" style={{height: "300px"}}>
              <LoadingIndicator/>
            </div>
          </div>
        </Card>
    );
  }
  
  return (
      <Card
          href={href}
          icon={keyQuestionsIcon}
          title={customName ? customName : "KEY QUESTIONS YEAR ON YEAR"}
          chartOptions={
            <React.Fragment>
              <button
                  className="Option"
                  onClick={() => handleSetKeyQuestionsChartType("Bar")}
              >
                Bar
              </button>
              <button
                  className="Option"
                  onClick={() => handleSetKeyQuestionsChartType("Line")}
              >
                Line
              </button>
            </React.Fragment>
          }
      >
        <div className="CardBody" >
        <div className="ToggleFlex">
          <div className="BarToggle">
            {keyQuestionsPeriodToggles &&
              Object.entries(keyQuestionsPeriodToggles).map(
                ([key, value], index: number) => {
                  return (
                    <ToggleButton
                      key={index}
                      active={value}
                      toggle={() => handleKeyQuestionsPeriodToggle(key)}
                    >
                      <div
                        className="ToggleDot"
                        style={{
                          backgroundColor: getChartColors(keyQuestionsChartType)[index],
                        }}
                      ></div>
                      <div className="ToggleLabel">{key}</div>
                    </ToggleButton>
                  );
                }
              )}
          </div>
        </div>
        <div className="Chart"  style={{height: "300px"}}>
          <div className="ChartWrapper">
            {data && checkNullChartData(data.pieChartData.data) ? (
              <div style={{ minWidth: `${minWidth}px` }}>
                <ResponsiveContainer width="99%" height={300} key={uuid}>
                  {keyQuestionsChartType === "Bar" ? (
                    <BarChart
                      data={transformDataForGroupedBars()}
                      maxBarSize={65}
                      barGap={15}
                      barCategoryGap={10}
                      margin={{ top: 20, bottom: 30, right: 10, left: 10 }}
                      ref={ref}
                    >
                      <CartesianGrid vertical={false} strokeDasharray="3" />
                      <Tooltip content={<PortalTooltip formatter={
                          data.options.yAxisType === "Percentage" || data.options.yAxisType === "Score"
                            ? getValueFormatter(data.options.yAxisType)
                            : undefined
                        } />} />
                      <XAxis
                        dataKey="name"
                        tick={<Tick />}
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={getYAxisFormatter(data.options.yAxisType)}
                        domain={getYAxisDomain(data.options.yAxisType, data.otherChartData, keyQuestionsPeriodToggles ? Object.entries(keyQuestionsPeriodToggles).filter(([, v]) => v).map(([k]) => k) : undefined)}
                        ticks={getYAxisTicks(data.options.yAxisType, data.otherChartData, keyQuestionsPeriodToggles ? Object.entries(keyQuestionsPeriodToggles).filter(([, v]) => v).map(([k]) => k) : undefined)}
                      />
                      {keyQuestionsPeriodToggles &&
                        Object.entries(keyQuestionsPeriodToggles).map(
                          ([key, value], index: number) => {
                            if (value) {
                              return (
                                <Bar
                                  key={index}
                                  dataKey={key}
                                  fill={getChartColors(keyQuestionsChartType)[index]}
                                  radius={[3, 3, 0, 0]}
                                  isAnimationActive={false}
                                >
                                  <LabelList
                                    dataKey={key}
                                    position="top"
                                    formatter={getValueFormatter(data.options.yAxisType)}
                                  />
                                </Bar>
                              );
                            } else {
                              return <React.Fragment key={index} />;
                            }
                          }
                        )}
                    </BarChart>
                  ) : keyQuestionsChartType === "Line" ? (
                    <LineChart
                      data={transformDataForGroupedBars()}
                      margin={{ top: 20, bottom: 20, right: 10, left: 10 }}
                      ref={ref}
                    >
                      <CartesianGrid vertical={false} strokeDasharray="3" />
                      <Tooltip content={<PortalTooltip formatter={
                          data.options.yAxisType === "Percentage" || data.options.yAxisType === "Score"
                            ? getValueFormatter(data.options.yAxisType)
                            : undefined
                        } />} />
                      <XAxis
                        dataKey="name"
                        tick={<Tick />}
                        axisLine={false}
                        tickLine={false}
                        padding={{ left: 30, right: 30 }}
                        interval={0}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={getYAxisFormatter(data.options.yAxisType)}
                        domain={getYAxisDomain(data.options.yAxisType, data.otherChartData, keyQuestionsPeriodToggles ? Object.entries(keyQuestionsPeriodToggles).filter(([, v]) => v).map(([k]) => k) : undefined)}
                        ticks={getYAxisTicks(data.options.yAxisType, data.otherChartData, keyQuestionsPeriodToggles ? Object.entries(keyQuestionsPeriodToggles).filter(([, v]) => v).map(([k]) => k) : undefined)}
                      />
                      {keyQuestionsPeriodToggles &&
                        Object.entries(keyQuestionsPeriodToggles).map(
                          ([key, value], index: number) => {
                            if (value) {
                              return (
                                <Line
                                  key={index}
                                  dataKey={key}
                                  fill={getChartColors(keyQuestionsChartType)[index]}
                                  isAnimationActive={false}
                                  type="monotone"
                                  stroke={getChartColors(keyQuestionsChartType)[index]}
                                  dot={{
                                    stroke: getChartColors(keyQuestionsChartType)[index],
                                    strokeWidth: 2,
                                  }}
                                >
                                  <LabelList
                                    dataKey={key}
                                    position="top"
                                    formatter={getValueFormatter(data.options.yAxisType)}
                                  />
                                </Line>
                              );
                            } else {
                              return <React.Fragment key={index} />;
                            }
                          }
                        )}
                    </LineChart>
                  ) : (
                    <React.Fragment />
                  )}
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="NoChartData">
                <p>No results found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default KeyQuestionsYoY;